'use client';

import PeriodTabs from '@/app/(public)/pricing/period-tabs';
import PlanCard from '@/app/(public)/pricing/plan-card';
import {
  applyDiscount,
  VALIDITY_PERIOD_LABEL,
} from '@/app/(public)/pricing/plan-utils';
import TrialBanner from '@/app/(public)/pricing/trial-banner';
import {
  getUpgradeFee,
  Plan,
  PlanSku,
  type UpgradeFeeRes,
} from '@/app/actions/cart';
import OrderConfirmModal, {
  type UpgradeInfo,
} from '@/components/order-confirm-modal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDisclosure } from '@/hooks/use-disclosure';
import { formatPrice } from '@/lib/util';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import { ArrowUpCircleIcon, Loader2Icon, ShoppingBagIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

interface SubscribePlansProps {
  plans: Plan[];
}

const SubscribePlans = ({ plans }: SubscribePlansProps) => {
  const membershipPlans = useMemo(
    () =>
      plans
        .filter((p) => p.type === 'MEMBERSHIP')
        .sort((a, b) => (a.tier_rank ?? 0) - (b.tier_rank ?? 0)),
    [plans],
  );

  const [selectedPeriod, setSelectedPeriod] = useState<string>('one_month');
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    user,
    loading: authLoading,
    triggerLogin,
    updateUserInfo,
    showActivateCardModal,
  } = useAuth();

  const [modalProps, setModalProps] = useState<{
    skuId: string;
    productName: string;
    skuName: string;
    price: number;
    isUpgrade?: boolean;
    upgradeInfo?: UpgradeInfo;
    skipUpgrade?: boolean;
  } | null>(null);

  // 升级引导弹窗状态
  const [upgradeGuide, setUpgradeGuide] = useState<{
    plan: Plan;
    sku: PlanSku;
    feeRes: UpgradeFeeRes | null;
    loading: boolean;
  } | null>(null);

  const trialPlan = (() => {
    for (const plan of membershipPlans) {
      const trialSku = plan.skus.find((s) => s.sku_id === 'MEMBER_BASIC_1D');
      if (trialSku && (plan.tier_rank ?? 0) > 0) {
        return { plan, sku: trialSku };
      }
    }
    return null;
  })();

  // 直接购买（全价进卡包）
  const openBuyModal = (plan: Plan, sku: PlanSku) => {
    const price = applyDiscount(plan, sku);
    setModalProps({
      skuId: sku.sku_id,
      productName: plan.name,
      skuName: sku.name ?? VALIDITY_PERIOD_LABEL[sku.validity_period] ?? '',
      price,
      skipUpgrade: true,
    });
    setUpgradeGuide(null);
    onOpen();
  };

  // 走升级流程（差价）
  const openUpgradeModal = async (plan: Plan, sku: PlanSku) => {
    try {
      const feeRes = await getUpgradeFee(sku.sku_id);
      if (!feeRes.available) {
        toast.error(feeRes.reason || '该方案不支持升级', {
          position: 'top-center',
        });
        return;
      }
      setModalProps({
        skuId: sku.sku_id,
        productName: plan.name,
        skuName: sku.name ?? VALIDITY_PERIOD_LABEL[sku.validity_period] ?? '',
        price: feeRes.upgrade_fee,
        isUpgrade: true,
        upgradeInfo: {
          currentPlanName: feeRes.current_product_name ?? '当前方案',
          upgradeFee: feeRes.upgrade_fee,
          currentPaidAmount: feeRes.current_paid_amount,
          targetPrice: feeRes.target_price,
          remainingDays: feeRes.remaining_days,
          bonusDays: feeRes.bonus_days,
        },
      });
      setUpgradeGuide(null);
      onOpen();
    } catch {
      toast.error('获取升级费用失败', { position: 'top-center' });
    }
  };

  const openOrderModal = async (plan: Plan, sku: PlanSku) => {
    const userTier = user?.pointInfo?.tier_rank ?? 0;
    const planTier = plan.tier_rank ?? 0;
    const isPaidMember = !!(
      user?.membership && user?.pointInfo?.free_week_membership_expired
    );
    const isHigherTier = !!(isPaidMember && planTier > userTier);

    if (isHigherTier) {
      // 付费会员购买更高等级 → 弹引导弹窗
      setUpgradeGuide({ plan, sku, feeRes: null, loading: true });
      try {
        const feeRes = await getUpgradeFee(sku.sku_id);
        setUpgradeGuide({ plan, sku, feeRes, loading: false });
      } catch {
        setUpgradeGuide({ plan, sku, feeRes: null, loading: false });
      }
    } else {
      openBuyModal(plan, sku);
    }
  };

  const handleBuy = (plan: Plan, sku: PlanSku) => {
    if (!user && triggerLogin) {
      triggerLogin(() => openOrderModal(plan, sku));
    } else {
      openOrderModal(plan, sku);
    }
  };

  const handleTrialBuy = () => {
    if (!trialPlan) return;
    const { plan, sku } = trialPlan;

    if (!user && triggerLogin) {
      triggerLogin(() => openOrderModal(plan, sku));
    } else {
      openOrderModal(plan, sku);
    }
  };

  return (
    <>
      {trialPlan && (
        <TrialBanner
          trialPlan={trialPlan}
          showBanner={
            !authLoading &&
            (user?.pointInfo?.can_activate_card ?? true) &&
            user?.pointInfo?.sku_id !== 'MEMBER_BASIC_1D' &&
            !user?.pointInfo?.queued_cards?.some(
              (c) => c.product_info.sku_id === 'MEMBER_BASIC_1D',
            )
          }
          onBuy={handleTrialBuy}
        />
      )}

      <PeriodTabs
        plans={membershipPlans}
        selectedPeriod={selectedPeriod}
        onPeriodChange={setSelectedPeriod}
      />

      <motion.div
        className={cn('grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 mb-8', {
          'lg:grid-cols-3': membershipPlans.length === 3,
          'lg:grid-cols-4': membershipPlans.length >= 4,
        })}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {membershipPlans.map((plan, index) => (
          <PlanCard
            key={plan.product_id}
            plan={plan}
            selectedPeriod={selectedPeriod}
            user={user}
            onBuy={handleBuy}
            onLogin={triggerLogin}
            index={index}
          />
        ))}
      </motion.div>

      {/* 升级引导弹窗 */}
      <Dialog
        open={!!upgradeGuide}
        onOpenChange={(open) => {
          if (!open) setUpgradeGuide(null);
        }}
      >
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle>选择购买方式</DialogTitle>
          </DialogHeader>

          {upgradeGuide && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                您当前是
                {upgradeGuide.feeRes?.current_product_name ?? '付费'}
                会员，可以选择：
              </p>

              {/* 选项 1：升级当前会员 */}
              <button
                type="button"
                className="w-full text-left rounded-full border-2 border-primary/20 hover:border-primary/50 p-4 transition-colors"
                disabled={
                  upgradeGuide.loading || !upgradeGuide.feeRes?.available
                }
                onClick={() =>
                  openUpgradeModal(upgradeGuide.plan, upgradeGuide.sku)
                }
              >
                <div className="flex items-start gap-3">
                  <ArrowUpCircleIcon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium text-sm">
                      升级当前会员到{upgradeGuide.plan.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {upgradeGuide.loading ? (
                        <span className="flex items-center gap-1">
                          <Loader2Icon className="w-3 h-3 animate-spin" />
                          计算差价中...
                        </span>
                      ) : upgradeGuide.feeRes?.available ? (
                        <>
                          立即生效，只需补差价{' '}
                          <span className="font-medium text-primary">
                            {formatPrice(upgradeGuide.feeRes.upgrade_fee)}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">
                          {upgradeGuide.feeRes?.reason ?? '暂不可用'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>

              {/* 选项 2：购买会员卡 */}
              <button
                type="button"
                className="w-full text-left rounded-full border-2 border-border hover:border-primary/50 p-4 transition-colors"
                onClick={() =>
                  openBuyModal(upgradeGuide.plan, upgradeGuide.sku)
                }
              >
                <div className="flex items-start gap-3">
                  <ShoppingBagIcon className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium text-sm">
                      购买{upgradeGuide.plan.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      全价{' '}
                      {formatPrice(
                        applyDiscount(upgradeGuide.plan, upgradeGuide.sku),
                      )}
                      ，加入卡包，到期后手动激活使用
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {modalProps && (
        <OrderConfirmModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          skuId={modalProps.skuId}
          productName={modalProps.productName}
          skuName={modalProps.skuName}
          price={modalProps.price}
          isUpgrade={modalProps.isUpgrade}
          upgradeInfo={modalProps.upgradeInfo}
          isMembershipOrder
          isCurrentlyMember={
            !!user?.membership && !(user?.pointInfo?.can_activate_card ?? false)
          }
          onActivateCard={showActivateCardModal}
          skipUpgrade={modalProps.skipUpgrade}
          onSuccess={() => {
            updateUserInfo();
          }}
        />
      )}
    </>
  );
};

export default SubscribePlans;
